/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Input, ViewChild, AfterViewInit } from '@angular/core';
import { EnumDictionary, TextDictionary } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  AddressAutocompleteComponent,
  AddressComponents,
  foldAddressComponentsForXero,
  MapboxService,
} from '@gh/core-ui';
import { foldToAsciiForXeroTrackingInputTrimmed } from '@gh/core-util';
import { SalespersonSelectItem } from '../../../resources/hlp.model';
import { HlpDetailsBusinessService } from '../../../resources/hlp-details-business.service';
import { map$ } from '@gh/rx/operators';
import { ListingStatus } from '@gh/builder-shared-data';

@Component({
  standalone: false,
  selector: 'gh-hlp-details-common',
  templateUrl: 'hlp-details-common-tab.component.html',
  styleUrls: ['hlp-details-common-tab.component.scss'],
})
export class HlpDetailsCommonTabComponent implements AfterViewInit {
  @Input() listingStatuses: EnumDictionary<ListingStatus>;
  @Input() states: TextDictionary;
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  mapCoordinates: number[] | null;
  isUserDragUpdate: boolean = false;
  showManualAddressFields = false;

  constructor(
    public businessService: HlpDetailsBusinessService,
    private mapboxService: MapboxService
  ) {
  }


  ngAfterViewInit() {
    setTimeout(() => {
      this.onAddressChange();
      
      const commonDetailsForm = this.businessService.mainForm.get('commonDetails');
      if (commonDetailsForm) {
        const hasAddressData = !!(commonDetailsForm.get('siteSuburb')?.value || 
                                 commonDetailsForm.get('siteStreetNumber')?.value || 
                                 commonDetailsForm.get('siteStreet')?.value || 
                                 commonDetailsForm.get('siteState')?.value ||
                                 commonDetailsForm.get('sitePostcode')?.value);
        this.showManualAddressFields = hasAddressData;
      }
    });
  }

  loadSalesPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findSalesPersonsByName(event.query, {
      showInactive: false,
    }).pipe(map$((salesPersons) => {
      const control = this.businessService.mainForm.get('commonDetails.alternateSalesperson');
      return control.value ? salesPersons.filter(
      (item) => item.id !== this.businessService.mainForm.get('commonDetails.alternateSalesperson')?.value.id,
    ) : salesPersons;
    })));
  }

  loadAlternateSalesPersons(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findSalesPersonsByName(event.query, {
      showInactive: false,
    }).pipe(map$((salesPersons) => {
      const control = this.businessService.mainForm.get('commonDetails.salesperson');
      return control.value ? salesPersons.filter((item) =>
            item.id !== this.businessService.mainForm.get('commonDetails.salesperson')?.value.id) : salesPersons;
    })));
  }

  onPrimarySalesPersonSelected(salesPerson: SalespersonSelectItem): void {
    if (!salesPerson) {
       this.businessService.mainForm.get('commonDetails.salesperson').setValue(undefined);
    }
  }

  onAlternateSalesPersonSelected(salesPerson: SalespersonSelectItem): void {
    if (!salesPerson) {
      this.businessService.mainForm.get('commonDetails.alternateSalesperson').setValue(undefined);
    }
  }

  onAddressSelected(components: AddressComponents, isUserDrag: boolean = false): void { 
    if (!components) return;

    const c = foldAddressComponentsForXero(components);
  
    const commonDetailsForm = this.businessService.mainForm.get('commonDetails');
    if (!commonDetailsForm) return;
  
    const fieldMappings: { formKey: string, componentKey: keyof AddressComponents }[] = [
      { formKey: 'siteStreetNumber', componentKey: 'streetNo' },
      { formKey: 'siteStreet', componentKey: 'street' },
      { formKey: 'siteSuburb', componentKey: 'city' },
      { formKey: 'sitePostcode', componentKey: 'postalCode' },
      { formKey: 'siteState', componentKey: 'state' },
    ];
  
    fieldMappings.forEach(({ formKey, componentKey }) => {
      const value = c[componentKey];
      const control = commonDetailsForm.get(formKey);
      if (control && value != null) control.setValue(value);
    });
  
    if (c.coordinates) {
      const [lng, lat] = c.coordinates;
      const latitudeControl = commonDetailsForm.get('latitude');
      const longitudeControl = commonDetailsForm.get('longitude');
      
      if (latitudeControl && longitudeControl) {
        latitudeControl.setValue(lat);
        longitudeControl.setValue(lng);
        
        if (latitudeControl.valid && longitudeControl.valid) {
          this.mapCoordinates = c.coordinates;
          this.isUserDragUpdate = isUserDrag;
        } else {
          this.mapCoordinates = null;
          latitudeControl.setValue(null);
          longitudeControl.setValue(null);
        }
      }
    }

    ['siteStreetNumber', 'siteStreet', 'siteSuburb', 'sitePostcode', 'siteState', 'latitude', 'longitude'].forEach(field => {
    const control = commonDetailsForm.get(field);
    if (control) control.markAsDirty();
    });
  }
  
  onCoordinatesChanged(coordinates: [number, number]): void {
    this.mapboxService.reverseGeocode(coordinates).subscribe(result => {
      const feature = result?.features?.[0];
      if (!feature) return;
      const components = this.mapboxService.getUpdatedComponents(result);
      components.coordinates = coordinates.map(c => +c.toFixed(6)) as [number, number];
      this.onAddressSelected(components, true);
      if (this.addressAutocomplete) {
        this.addressAutocomplete.inputValue = foldToAsciiForXeroTrackingInputTrimmed(feature.place_name);
      }
    });
  }
  
  onAddressChange(): void {
    const commonDetailsForm = this.businessService.mainForm.get('commonDetails');
    const autocomplete = this.addressAutocomplete;
  
    if (!commonDetailsForm || !autocomplete) return;
  
    const getValue = (field: string) => commonDetailsForm.get(field)?.value?.trim() || '';
  
    const streetNumber = getValue('siteStreetNumber');
    const street = getValue('siteStreet');
    const suburb = getValue('siteSuburb');
    const state = getValue('siteState');
    const postCode = getValue('sitePostcode')
  
    const streetAddress = [streetNumber, street].filter(Boolean).join(' ');
    const parts = [streetAddress, suburb, state, postCode];
  
    autocomplete.inputValue = parts.filter(Boolean).join(', ');

    const latitudeControl = commonDetailsForm.get('latitude');
    const longitudeControl = commonDetailsForm.get('longitude');
    
    if (latitudeControl && longitudeControl && 
        latitudeControl.valid && longitudeControl.valid) {
      this.mapCoordinates = [longitudeControl.value, latitudeControl.value];
    } else {
      this.mapCoordinates = null;
    }
  }
}
